/**
 * 진행중 품의에서 기안자에게 노출하는 두 조치(회수 / 반려요청)의 노출 판정.
 *
 * 둘은 상보 관계다 — 기안자 외 결재자가 아무도 결재하지 않았으면 회수, 한 명이라도 결재했으면
 * 반려요청이다. 조회 화면의 우상단 버튼과 더보기 메뉴가 같은 판정을 써야 하므로 한 곳에 모았다.
 * 판정 기준(기안자 제외 + status !== '0')은 서버 회수·반려요청 검증과 동일하다.
 */

/** 결재자 최소 형태 — status 는 상세의 ApproverStatus 유니온을 그대로 받도록 string 으로 둔다. */
type ApproverLike = {
  passportId: string;
  status: string;
};

type OngoingActionDoc = {
  status: string;
  type?: { code?: number } | null;
  author?: { passportId?: string } | null;
  approvers?: ApproverLike[] | null;
};

/** 회수·반려요청이 열리는 문서 상태 — 완료·반려·취소·삭제·임시저장은 대상이 아니다. */
const ONGOING_STATUSES: readonly string[] = ['P', 'H'];

/**
 * 회수만 막는 레거시 양식 예외. 209 는 회수 시 후속 처리가 레거시에 묶여 있어 제외한다.
 * 반려요청은 서버 검증에도 양식 예외가 없어 적용하지 않는다.
 */
const RECALL_EXCLUDED_TYPE_CODE = 209;

/** 기안자 본인이 보고 있는 진행중(P)·보류중(H) 문서인지 — 두 판정의 공통 전제. */
const isAuthorViewingOngoing = (
  doc: OngoingActionDoc,
  myPassportId: string | null | undefined,
): boolean => {
  const authorPassportId = doc.author?.passportId;
  if (!myPassportId || !authorPassportId) return false;
  if (authorPassportId !== myPassportId) return false;
  return ONGOING_STATUSES.includes(doc.status);
};

/**
 * 기안자를 제외한 결재자 중 한 명이라도 결재 행위(승인/반려/보류)를 했는지.
 * 기안자는 상신 시 자동승인(C)이라 반드시 제외해야 한다 — 안 빼면 항상 '결재함'이 된다.
 */
const hasOtherApproverActed = (doc: OngoingActionDoc): boolean => {
  const authorPassportId = doc.author?.passportId;
  return (doc.approvers ?? []).some(
    (a) => a.passportId !== authorPassportId && a.status !== '0',
  );
};

/** 회수 노출 — 기안자 본인 + P/H + 아무도 결재 안 함 + 양식 209 아님. */
export const canRecallDocument = (
  doc: OngoingActionDoc,
  myPassportId: string | null | undefined,
): boolean => {
  if (!isAuthorViewingOngoing(doc, myPassportId)) return false;
  if (doc.type?.code === RECALL_EXCLUDED_TYPE_CODE) return false;
  return !hasOtherApproverActed(doc);
};

/** 반려요청 노출 — 기안자 본인 + P/H + 결재한 결재자 1명 이상. */
export const canRequestReturnDocument = (
  doc: OngoingActionDoc,
  myPassportId: string | null | undefined,
): boolean => {
  if (!isAuthorViewingOngoing(doc, myPassportId)) return false;
  return hasOtherApproverActed(doc);
};
